import React, { useState, useCallback, useEffect, useContext } from 'react';
import { Typography, Table, Select, DatePicker, Space, Button, message } from 'antd';
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import api from '../api';
import { UserContext } from '../App';

const { RangePicker } = DatePicker;

// Completed records from the start of the month three months back, through
// today. Three whole months rather than a rolling ninety days: these are read
// as monthly columns, and a part-month at the left edge reads as a quiet month
// rather than a truncated one.
const defaultRange = () => [dayjs().subtract(3, 'month').startOf('month'), dayjs()];

// Fixed order, so the columns read as an escalation left to right.
const WARNINGS = ['1st', '2nd', '3rd', 'Final'];

const monthLabel = (mk) => {
  try { return dayjs(`${mk}-01`).format('MMM YYYY'); } catch { return mk; }
};

export default function IncidentStatsPage() {
  const currentUser = useContext(UserContext);

  const [rows, setRows]                 = useState([]);
  const [warnRows, setWarnRows]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [teamFilter, setTeamFilter]     = useState(undefined);
  const [assignedToId, setAssignedToId] = useState(null);
  const [dateRange, setDateRange]       = useState(defaultRange);
  const [teamOptions, setTeamOptions]   = useState([]);
  const [assignedToOpts, setAssignedToOpts] = useState([]);

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then((r) => {
      const all = r.data || [];
      const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
      const visible = vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
      setTeamOptions(visible.map((t) => ({ label: t.Name, value: t.ID })));
    }).catch(() => {});
  }, [currentUser]);

  useEffect(() => {
    api.get('/api/compliance/filter-options', { params: { type: 'incident' } })
      .then((r) => setAssignedToOpts((r.data.assigned_tos ?? []).map((u) => ({ label: u.name, value: u.id }))))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: 'incident' };
      if (teamFilter)     params.team_id             = teamFilter;
      if (assignedToId)   params.assigned_to_user_id = assignedToId;
      if (dateRange?.[0]) params.date_from           = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.date_to             = dateRange[1].format('YYYY-MM-DD');
      // Active warnings take the team and Assigned To filters but not the
      // dates: a warning is active until set Inactive, whenever it was issued.
      const { date_from, date_to, ...warnParams } = params;
      const [res, warnRes] = await Promise.all([
        api.get('/api/compliance/stats', { params }),
        api.get('/api/compliance/stats/active-warnings', { params: warnParams }),
      ]);
      setRows(res.data || []);
      setWarnRows(warnRes.data || []);
    } catch {
      message.error('Failed to load compliance stats.');
      setRows([]);
      setWarnRows([]);
    } finally {
      setLoading(false);
    }
  }, [teamFilter, assignedToId, dateRange]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Employee name is not offered: these are counts, so filtering by one person
  // would narrow every row to them and the pivot would stop being a pivot.
  const months = [...new Set(rows.map((r) => r.MonthKey).filter(Boolean))].sort();

  const byKey = {};
  rows.forEach((r) => {
    const k = `${r.TeamName}||${r.IssueName}`;
    if (!byKey[k]) byKey[k] = { _key: k, TeamName: r.TeamName, IssueName: r.IssueName, Total: 0 };
    byKey[k][r.MonthKey] = (byKey[k][r.MonthKey] || 0) + r.Qty;
    byKey[k].Total += r.Qty;
  });
  const pivot = Object.values(byKey).sort((a, b) =>
    a.TeamName.localeCompare(b.TeamName) || a.IssueName.localeCompare(b.IssueName));

  const monthTotals = {};
  let grandTotal = 0;
  rows.forEach((r) => {
    monthTotals[r.MonthKey] = (monthTotals[r.MonthKey] || 0) + r.Qty;
    grandTotal += r.Qty;
  });

  const columns = [
    {
      // Merged down the team so a team with several issue types reads as one
      // block rather than a repeated name.
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 170, fixed: 'left',
      onCell: (r, i) => {
        const prev = i > 0 ? pivot[i - 1].TeamName : null;
        if (r.TeamName === prev) return { rowSpan: 0 };
        let span = 1;
        while (i + span < pivot.length && pivot[i + span].TeamName === r.TeamName) span++;
        return { rowSpan: span };
      },
      render: (v) => <Typography.Text strong>{v}</Typography.Text>,
    },
    { title: 'Issue Type', dataIndex: 'IssueName', key: 'IssueName', width: 220, fixed: 'left', ellipsis: true },
    ...months.map((mk) => ({
      title: monthLabel(mk), key: mk, width: 110, align: 'center',
      render: (_, r) => r[mk] ? r[mk] : <Typography.Text type="secondary">—</Typography.Text>,
    })),
    {
      title: 'Total', dataIndex: 'Total', key: 'Total', width: 90, align: 'right',
      sorter: (a, b) => a.Total - b.Total,
      render: (v) => <Typography.Text strong>{v}</Typography.Text>,
    },
  ];

  // ── Active warnings: one row per team + agent, a column per warning ──────
  const warnByKey = {};
  warnRows.forEach((r) => {
    const k = `${r.TeamName}||${r.EmployeeUserID}`;
    if (!warnByKey[k]) warnByKey[k] = { _key: k, TeamName: r.TeamName, EmployeeName: r.EmployeeName || '', Total: 0 };
    warnByKey[k][r.WarningNumber] = (warnByKey[k][r.WarningNumber] || 0) + r.Qty;
    warnByKey[k].Total += r.Qty;
  });
  const warnPivot = Object.values(warnByKey).sort((a, b) =>
    a.TeamName.localeCompare(b.TeamName) || a.EmployeeName.localeCompare(b.EmployeeName));
  const warnTotals = {};
  let warnGrand = 0;
  warnRows.forEach((r) => {
    warnTotals[r.WarningNumber] = (warnTotals[r.WarningNumber] || 0) + r.Qty;
    warnGrand += r.Qty;
  });

  const warnColumns = [
    {
      title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 170, fixed: 'left',
      onCell: (r, i) => {
        const prev = i > 0 ? warnPivot[i - 1].TeamName : null;
        if (r.TeamName === prev) return { rowSpan: 0 };
        let span = 1;
        while (i + span < warnPivot.length && warnPivot[i + span].TeamName === r.TeamName) span++;
        return { rowSpan: span };
      },
      render: (v) => <Typography.Text strong>{v}</Typography.Text>,
    },
    { title: 'Agent Name', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 220, fixed: 'left', ellipsis: true },
    ...WARNINGS.map((w) => ({
      title: w, key: w, width: 90, align: 'center',
      render: (_, r) => r[w] ? r[w] : <Typography.Text type="secondary">—</Typography.Text>,
    })),
    {
      title: 'Total', dataIndex: 'Total', key: 'Total', width: 90, align: 'right',
      sorter: (a, b) => a.Total - b.Total,
      render: (v) => <Typography.Text strong>{v}</Typography.Text>,
    },
  ];

  const warnSummary = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        {WARNINGS.map((w, i) => (
          <Table.Summary.Cell key={w} index={2 + i} align="center">
            <Typography.Text strong>{warnTotals[w] || 0}</Typography.Text>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={2 + WARNINGS.length} align="right">
          <Typography.Text strong>{warnGrand}</Typography.Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  const exportToExcel = () => {
    const out = pivot.map((r) => {
      const base = { Team: r.TeamName, 'Issue Type': r.IssueName };
      months.forEach((mk) => { base[monthLabel(mk)] = r[mk] || 0; });
      base.Total = r.Total;
      return base;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(out), 'Compliance Stats');
    const warnOut = warnPivot.map((r) => {
      const base = { Team: r.TeamName, 'Agent Name': r.EmployeeName };
      WARNINGS.forEach((w) => { base[w] = r[w] || 0; });
      base.Total = r.Total;
      return base;
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(warnOut), 'Active Warning');
    XLSX.writeFile(wb, 'compliance-stats.xlsx');
  };

  const summary = () => (
    <Table.Summary fixed>
      <Table.Summary.Row>
        <Table.Summary.Cell index={0}><Typography.Text strong>Total</Typography.Text></Table.Summary.Cell>
        <Table.Summary.Cell index={1} />
        {months.map((mk, i) => (
          <Table.Summary.Cell key={mk} index={2 + i} align="center">
            <Typography.Text strong>{monthTotals[mk] || 0}</Typography.Text>
          </Table.Summary.Cell>
        ))}
        <Table.Summary.Cell index={2 + months.length} align="right">
          <Typography.Text strong>{grandTotal}</Typography.Text>
        </Table.Summary.Cell>
      </Table.Summary.Row>
    </Table.Summary>
  );

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Compliance Stats</Typography.Title>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
        Completed compliance incidents by month, counted per issue type. An incident carrying more than one
        issue type is counted under each, so these are issue counts rather than record counts.
      </Typography.Paragraph>

      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder="All teams"
          options={teamOptions}
          value={teamFilter}
          onChange={setTeamFilter}
          style={{ width: 180 }}
        />
        <Select
          allowClear showSearch optionFilterProp="label"
          placeholder="Assigned To"
          options={assignedToOpts}
          value={assignedToId}
          onChange={setAssignedToId}
          style={{ width: 180 }}
        />
        <RangePicker
          value={dateRange}
          onChange={setDateRange}
          format="YYYY-MM-DD"
          placeholder={['Incident from', 'Incident to']}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={load} loading={loading}>Search</Button>
        <Button icon={<DownloadOutlined />} onClick={exportToExcel} disabled={pivot.length === 0 && warnPivot.length === 0}>Export</Button>
      </Space>

      <Table
        rowKey="_key"
        dataSource={pivot}
        columns={columns}
        loading={loading}
        size="small"
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        sticky
        summary={pivot.length > 0 ? summary : undefined}
        locale={{ emptyText: 'No completed compliance incidents in this date range.' }}
      />

      <Typography.Title level={5} style={{ marginTop: 32, marginBottom: 12 }}>Active Warning (date range is not applied)</Typography.Title>
      <Table
        rowKey="_key"
        dataSource={warnPivot}
        columns={warnColumns}
        loading={loading}
        size="small"
        pagination={false}
        bordered
        scroll={{ x: 'max-content' }}
        summary={warnPivot.length > 0 ? warnSummary : undefined}
        locale={{ emptyText: 'No active warnings.' }}
      />
    </>
  );
}
